import { openDB, deleteDB, wrap, unwrap, IDBPDatabase } from 'idb';
import { devlog } from './utils.ts';

// Define types for our database operations
type MeshID = string;
type AppID = string;

// Default value for when no previous app exists
const NO_PREVIOUS_APP: AppID = 'none';

/**
 * Data structure for transferring or saving mesh data
 * Used when saving a mesh for transfer
 */
export interface TransferSaveData {
  authorId: string;
  meshName: string;
  meshId: MeshID;
  currentApp: AppID;  // The app this mesh is currently in
  destinationApp: AppID;  // The app the mesh is being sent to
  stlData: Blob;
}

/**
 * Complete mesh data as stored in the database
 * Includes additional fields like id and lastSav. 'currentApp' gets turned into 'lastApp' here.
 */
export interface StoredMeshData extends Omit<TransferSaveData, 'currentApp'> {
  id: number;
  lastSave: Date;
  lastApp: AppID;  // The app the mesh was last in
  autoLoad?: boolean;  // Whether to automatically load this mesh
  queueDelete?: boolean; // Whether this mesh should be deleted after loading
}

// Database schema version
const DB_VERSION = 1;
const DB_NAME = 'appsuite';
const MESH_STORE_NAME = 'meshes';

/**
 * Initializes the IndexedDB database with the required schema
 * Creates an index on meshId for efficient lookups
 * Returns a promise that resolves to the database instance
 */
export const initDB = (): Promise<IDBPDatabase> => {
  return openDB(DB_NAME, DB_VERSION, {
    upgrade(db) {
      // Create the object store if it doesn't exist
      if (!db.objectStoreNames.contains(MESH_STORE_NAME)) {
        const store = db.createObjectStore(MESH_STORE_NAME, { keyPath: 'id', autoIncrement: true });
        store.createIndex('meshId', 'meshId', { unique: true });
        store.createIndex('lastSave', 'lastSave', { unique: false });
      }
    }
  });
};

// Initialize the database
export const db = initDB();

/**
 * Stores or updates a mesh in the database
 * If a mesh with the same meshId exists, it will be updated
 * Otherwise, a new mesh entry will be created
 * @param meshData - The mesh data to store
 * @param autoLoad - Optional flag to indicate if this mesh should be automatically loaded
 */
export const storeMesh = async (meshData: TransferSaveData, autoLoad?: boolean): Promise<StoredMeshData> => {
  // Validation: Check if lastApp and destinationApp are the same (which indicates an error)
  if (meshData.currentApp === meshData.destinationApp) {
    const errorMsg = `Mesh storage aborted: currentApp and destinationApp cannot be the same (${meshData.currentApp}). This indicates a logic error in mesh transfer.`;
    console.error(errorMsg);
    throw new Error(errorMsg);
  }

  // Validation: Check if destinationApp is still set to 'none' (user hasn't selected a destination)
  if (meshData.destinationApp === 'none') {
    const errorMsg = `Mesh storage aborted: destinationApp is still 'none'. User must select a destination app before saving.`;
    console.error(errorMsg);
    throw new Error(errorMsg);
  }

  const database = await db;
  const tx = database.transaction(MESH_STORE_NAME, 'readwrite');
  const store = tx.objectStore(MESH_STORE_NAME);
  const meshIndex = store.index('meshId');
  
  // Check if mesh already exists
  const existingMesh = await meshIndex.get(meshData.meshId);
  
  if (existingMesh) {
    // Update existing mesh - set properties manually to ensure correct typing
    existingMesh.meshName = meshData.meshName;
    existingMesh.authorId = meshData.authorId;
    existingMesh.lastApp = meshData.currentApp;  // Update lastApp to current app from incoming data
    existingMesh.destinationApp = meshData.destinationApp;  // Set new destination
    existingMesh.stlData = meshData.stlData;
    existingMesh.lastSave = new Date();
    
    // Set autoLoad flag if provided
    if (autoLoad !== undefined) {
      existingMesh.autoLoad = autoLoad;
    }
    
    await store.put(existingMesh);
    await tx.done;
    devlog(`Updated existing mesh ${meshData.meshId} from ${meshData.currentApp} to ${meshData.destinationApp}`);
    return existingMesh;
  } else {
    // Create new mesh without including currentApp
    // Extract all properties except currentApp (which becomes lastApp)
    const { currentApp, ...restOfMeshData } = meshData;
    
    // Create the StoredMeshData object with proper naming
    const newMesh: Omit<StoredMeshData, 'id'> = {
      ...restOfMeshData,
      lastApp: currentApp,  // currentApp from TransferSaveData becomes lastApp in StoredMeshData
      lastSave: new Date(),
      autoLoad: autoLoad ?? false  // Default to false if not provided
    };
    
    const id = await store.add(newMesh) as number;
    await tx.done;
    
    devlog(`Created new mesh ${meshData.meshId} from ${currentApp} to ${meshData.destinationApp}`);
    return {
      ...newMesh,
      id,
    };
  }
};

/**
 * Sets the destination app for a mesh, updating the last app in the process
 * lastApp becomes the previous destinationApp value
 * @param meshId - The ID of the mesh to update
 * @param nextApp - The app ID to set as the new destination
 * @param autoLoad - Optional flag to indicate if this mesh should be automatically loaded
 */
export const setNewApp = async (meshId: MeshID, nextApp: AppID, autoLoad?: boolean): Promise<void> => {
  const database = await db;
  const tx = database.transaction(MESH_STORE_NAME, 'readwrite');
  const store = tx.objectStore(MESH_STORE_NAME);
  const meshIndex = store.index('meshId');
  
  const mesh = await meshIndex.get(meshId);
  if (mesh) {
    // Update lastApp to the current destinationApp before changing it
    // Only do this if the current destinationApp is not the default value
    if (mesh.destinationApp !== NO_PREVIOUS_APP) {
      devlog(`Updating mesh ${meshId}: lastApp from ${mesh.lastApp} to ${mesh.destinationApp}`);
      mesh.lastApp = mesh.destinationApp;
    }
    
    devlog(`Setting mesh ${meshId} destinationApp to ${nextApp}`);
    mesh.destinationApp = nextApp;
    mesh.lastSave = new Date(); // Update the save timestamp
    
    // Set autoLoad flag if provided
    if (autoLoad !== undefined) {
      devlog(`Setting mesh ${meshId} autoLoad to ${autoLoad}`);
      mesh.autoLoad = autoLoad;
    }
    
    await store.put(mesh);
  }
  await tx.done;
};

/**
 * Deletes a specific mesh from the database
 */
export const deleteMesh = async (meshId: MeshID): Promise<void> => {
  const database = await db;
  const tx = database.transaction(MESH_STORE_NAME, 'readwrite');
  const store = tx.objectStore(MESH_STORE_NAME);
  const meshIndex = store.index('meshId');
  
  const mesh = await meshIndex.get(meshId);
  if (mesh) {
    await store.delete(mesh.id);
    devlog(`Deleted mesh ${meshId}`);
  }
  await tx.done;
};

/**
 * Deletes all meshes from the database
 */
export const deleteAllStoredTransfers = async (): Promise<void> => {
  const database = await db;
  const tx = database.transaction(MESH_STORE_NAME, 'readwrite');
  const store = tx.objectStore(MESH_STORE_NAME);
  
  // Count records before delete
  const countBefore = await store.count();
  devlog(`Clearing IndexedDB store: ${MESH_STORE_NAME} (${countBefore} records)`);
  
  await store.clear();
  await tx.done;
  
  // Verify clear worked by checking count after
  const verifyTx = database.transaction(MESH_STORE_NAME, 'readonly');
  const verifyStore = verifyTx.objectStore(MESH_STORE_NAME);
  const countAfter = await verifyStore.count();
  devlog(`IndexedDB clear result: ${countAfter === 0 ? 'SUCCESS' : 'FAILED'} (${countAfter} records remaining)`);
};

/**
 * Checks for active meshes in a specific app and returns the latest saved one
 * Returns both the latest mesh and the total count of meshes found
 * If onlyLatest is false, returns all meshes for the app in the meshes array
 * If findAutoLoad is true, will prioritize returning meshes with autoLoad=true
 */
export const checkForStoredMeshData = async (
  app: AppID, 
  onlyLatest: boolean = true,
  findAutoLoad: boolean = false
): Promise<{ 
  latestMesh: StoredMeshData | null; 
  autoLoadMesh: StoredMeshData | null;
  totalCount: number; 
  meshes?: StoredMeshData[] 
}> => {
  const database = await db;
  const tx = database.transaction(MESH_STORE_NAME, 'readonly');
  const store = tx.objectStore(MESH_STORE_NAME);
  
  // Get all meshes for this app
  const allMeshes = await store.getAll();
  const appMeshes = allMeshes.filter(mesh => mesh.destinationApp === app);
  
  if (appMeshes.length === 0) {
    return { 
      latestMesh: null, 
      autoLoadMesh: null,
      totalCount: 0, 
      meshes: [] 
    };
  }
  
  // Sort by lastSave and get the latest
  const sortedMeshes = appMeshes.sort((a, b) => b.lastSave.getTime() - a.lastSave.getTime());
  
  // Find mesh marked for auto-loading (if requested)
  let autoLoadMesh = null;
  if (findAutoLoad) {
    autoLoadMesh = sortedMeshes.find(mesh => mesh.autoLoad === true) || null;
    if (autoLoadMesh) {
      devlog(`Found autoLoad mesh for ${app}: ${autoLoadMesh.meshId}`);
    }
  }
  
  return {
    latestMesh: sortedMeshes[0],
    autoLoadMesh,
    totalCount: appMeshes.length,
    meshes: onlyLatest ? undefined : sortedMeshes
  };
};

/**
 * Retrieves the STL data for a specific mesh
 */
export const stlFromDB = async (meshId: MeshID): Promise<Blob | null> => {
  const database = await db;
  const tx = database.transaction(MESH_STORE_NAME, 'readonly');
  const store = tx.objectStore(MESH_STORE_NAME);
  const meshIndex = store.index('meshId');
  
  const mesh = await meshIndex.get(meshId);
  return mesh?.stlData ?? null;
};

/**
 * Retrieves the sender app (lastApp) for a specific mesh
 * @param meshId - The ID of the mesh to get the sender app for
 * @returns The app ID that sent this mesh, or null if mesh not found
 */
export const getSenderApp = async (meshId: MeshID): Promise<AppID | null> => {
  const database = await db;
  const tx = database.transaction(MESH_STORE_NAME, 'readonly');
  const store = tx.objectStore(MESH_STORE_NAME);
  const meshIndex = store.index('meshId');
  
  const mesh = await meshIndex.get(meshId);
  return mesh?.lastApp ?? null;
};

// /**
//  * Creates a test mesh for restore testing
//  * Sets lastApp to 'test' and destinationApp to the provided appId
//  * @param appId - The app ID to set as destinationApp
//  * @param meshData - Optional mesh data to include (stlData, meshName, etc.)
//  */
// export const createRestoreTest = async (appId: AppID, meshData?: Partial<Omit<TransferSaveData, 'currentApp'>>): Promise<StoredMeshData> => {
//   const testMesh: TransferSaveData = {
//     authorId: 'test',
//     meshName: 'Restore Test Mesh',
//     meshId: `restore-test-${appId}-${Date.now()}`,
//     currentApp: meshData?.currentApp,  // Set current app
//     stlData: null,
//     destinationApp: appId,
//     ...meshData  // Override defaults with provided mesh data
//   };

//   return storeMesh(testMesh);
// };